/** Presentation helpers for the public event detail template. No event data. */

export type EventDetailFirstFoldSlot =
  | "category"
  | "title"
  | "when"
  | "menu"
  | "price"
  | "cta";

/** Structural first-fold order. Flyer, About, and address stay after this. */
export function eventDetailFirstFoldSlots({
  hasMenu,
  hasPrice,
  hasCta,
}: {
  hasMenu: boolean;
  hasPrice: boolean;
  hasCta: boolean;
}): EventDetailFirstFoldSlot[] {
  const slots: EventDetailFirstFoldSlot[] = ["category", "title", "when"];
  if (hasMenu) slots.push("menu");
  if (hasPrice) slots.push("price");
  if (hasCta) slots.push("cta");
  return slots;
}

export function formatTimeTo12Hour(timeString: string): string {
  const [hours, minutes] = timeString.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes} ${period}`;
}

export function formatEventWhenLine({
  date,
  startTime,
  endTime,
}: {
  date: Date;
  startTime: string;
  endTime?: string;
}): string {
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const monthDay = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
  const start = formatTimeTo12Hour(startTime);

  if (endTime) {
    return `${weekday} · ${monthDay} · ${start} – ${formatTimeTo12Hour(endTime)}`;
  }

  return `${weekday} · ${monthDay} · ${start}`;
}

export function splitMenuHighlights(menuHighlight: string): string[] {
  return menuHighlight
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function collapseWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** True when the description only restates the title (or a Join-us-for title line). */
export function isTitleBoilerplateDescription(
  description: string | undefined,
  title: string,
): boolean {
  if (!description?.trim()) return true;

  const desc = collapseWhitespace(description);
  const normalizedTitle = collapseWhitespace(title);
  if (!normalizedTitle) return false;

  if (desc.toLowerCase() === normalizedTitle.toLowerCase()) return true;

  const titlePattern = escapeRegExp(normalizedTitle);
  const joinUs = new RegExp(`^join us for (?:the )?${titlePattern}\\b`, "i");

  if (joinUs.test(desc)) {
    const rest = desc
      .replace(joinUs, "")
      .replace(/^[\s,.:;!?…-]+/, "")
      .trim();

    if (!rest) return true;
    // "at {restaurant}." restates location, not extra event copy.
    if (/^at\b/i.test(rest)) return true;
    return false;
  }

  const withoutTitle = desc
    .replace(new RegExp(titlePattern, "ig"), "")
    .replace(/[\s,.:;!?…-]+/g, " ")
    .trim();

  return withoutTitle.length === 0;
}
